import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { OrangeSection } from '../components/ui/OrangeSection'
import { 
  CheckSquare, 
  Plus, 
  Edit, 
  Trash2, 
  Copy,
  Calendar,
  Clock,
  User,
  Filter,
  Search,
  CheckCircle,
  Circle,
  AlertTriangle,
  BarChart3,
  Target,
  Users,
  FileText
} from 'lucide-react'

interface ChecklistItem {
  id: string
  title: string
  description: string
  completed: boolean
  dueDate?: string
  assignedTo?: string
  priority: 'low' | 'medium' | 'high'
  category: string
  createdAt: string
  updatedAt: string
}

interface Checklist {
  id: string
  name: string
  description: string
  items: ChecklistItem[]
  status: 'active' | 'completed' | 'archived'
  createdBy: string
  createdAt: string
  updatedAt: string
  progress: number
}

const mockChecklists: Checklist[] = [
  {
    id: '1',
    name: 'Daily Inventory Check',
    description: 'Daily tasks for inventory management',
    status: 'active',
    createdBy: 'John Doe',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
    progress: 60,
    items: [
      {
        id: '1-1',
        title: 'Check low stock items',
        description: 'Review items with quantity < 5',
        completed: true,
        dueDate: '2024-01-15',
        assignedTo: 'John Doe',
        priority: 'high',
        category: 'Inventory',
        createdAt: '2024-01-15T10:00:00Z',
        updatedAt: '2024-01-15T10:00:00Z'
      },
      {
        id: '1-2',
        title: 'Update pricing',
        description: 'Review and update item prices',
        completed: false,
        dueDate: '2024-01-15',
        assignedTo: 'Jane Smith',
        priority: 'medium',
        category: 'Pricing',
        createdAt: '2024-01-15T10:00:00Z',
        updatedAt: '2024-01-15T10:00:00Z'
      },
      {
        id: '1-3',
        title: 'Process returns',
        description: 'Handle any pending returns',
        completed: false,
        dueDate: '2024-01-15',
        assignedTo: 'Mike Johnson',
        priority: 'low',
        category: 'Returns',
        createdAt: '2024-01-15T10:00:00Z',
        updatedAt: '2024-01-15T10:00:00Z'
      }
    ]
  },
  {
    id: '2',
    name: 'Weekly Stock Audit',
    description: 'Comprehensive weekly inventory audit',
    status: 'active',
    createdBy: 'Jane Smith',
    createdAt: '2024-01-10T09:00:00Z',
    updatedAt: '2024-01-14T15:30:00Z',
    progress: 25,
    items: [
      {
        id: '2-1',
        title: 'Count all items in Warehouse A',
        description: 'Physical count of all items in Warehouse A',
        completed: false,
        dueDate: '2024-01-20',
        assignedTo: 'Team A',
        priority: 'high',
        category: 'Audit',
        createdAt: '2024-01-10T09:00:00Z',
        updatedAt: '2024-01-10T09:00:00Z'
      },
      {
        id: '2-2',
        title: 'Verify item locations',
        description: 'Ensure all items are in correct locations',
        completed: true,
        dueDate: '2024-01-18',
        assignedTo: 'Team B',
        priority: 'medium',
        category: 'Location',
        createdAt: '2024-01-10T09:00:00Z',
        updatedAt: '2024-01-14T15:30:00Z'
      }
    ]
  },
  {
    id: '3',
    name: 'Monthly Report Preparation',
    description: 'Tasks for monthly reporting',
    status: 'completed',
    createdBy: 'Mike Johnson',
    createdAt: '2024-01-01T08:00:00Z',
    updatedAt: '2024-01-31T17:00:00Z',
    progress: 100,
    items: [
      {
        id: '3-1',
        title: 'Generate sales report',
        description: 'Create monthly sales report',
        completed: true,
        dueDate: '2024-01-31',
        assignedTo: 'Mike Johnson',
        priority: 'high',
        category: 'Reporting',
        createdAt: '2024-01-01T08:00:00Z',
        updatedAt: '2024-01-31T17:00:00Z'
      }
    ]
  }
]

export default function ChecklistsPage() {
  const [checklists, setChecklists] = useState<Checklist[]>(mockChecklists)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedChecklists, setSelectedChecklists] = useState<string[]>([])

  const filteredChecklists = checklists.filter(checklist => {
    const matchesSearch = checklist.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         checklist.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || checklist.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const handleItemToggle = (checklistId: string, itemId: string) => {
    setChecklists(prev => prev.map(checklist => {
      if (checklist.id === checklistId) {
        const updatedItems = checklist.items.map(item => 
          item.id === itemId ? { ...item, completed: !item.completed } : item
        )
        const completedCount = updatedItems.filter(item => item.completed).length
        const progress = (completedCount / updatedItems.length) * 100
        return { ...checklist, items: updatedItems, progress }
      }
      return checklist
    }))
  }

  const handleChecklistToggle = (checklistId: string) => {
    if (selectedChecklists.includes(checklistId)) {
      setSelectedChecklists(prev => prev.filter(id => id !== checklistId))
    } else {
      setSelectedChecklists(prev => [...prev, checklistId])
    }
  }

  const handleBulkAction = (action: string) => {
    console.log(`Bulk action: ${action} on checklists:`, selectedChecklists)
    // Implement bulk actions
  }

  const getStatusBadge = (status: Checklist['status']) => {
    const variants = {
      active: 'success',
      completed: 'info',
      archived: 'secondary'
    } as const

    return (
      <Badge variant={variants[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const getPriorityBadge = (priority: ChecklistItem['priority']) => {
    const variants = {
      low: 'secondary',
      medium: 'warning',
      high: 'error'
    } as const

    return (
      <Badge variant={variants[priority]} className="text-xs">
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </Badge>
    )
  }

  const getProgressColor = (progress: number) => {
    if (progress === 100) return 'bg-green-500'
    if (progress >= 75) return 'bg-blue-500'
    if (progress >= 50) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header Section */}
      <OrangeSection>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-white" />
            <h2 className="text-lg font-medium text-white">Task Management</h2>
          </div>
          <p className="text-sm text-gray-300">
            Create and manage checklists for inventory operations and workflows.
          </p>
        </div>
      </OrangeSection>

      {/* Search and Filters */}
      <div className="bg-black/40 backdrop-blur-sm border-2 border-orange-500/30 rounded-xl p-6 shadow-2xl shadow-orange-500/20 mb-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-orange-500/10 border border-orange-500/30">
              <Search className="h-5 w-5 text-orange-400 drop-shadow-[0_0_8px_rgba(251,146,60,0.8)]" />
            </div>
            <h3 className="text-lg font-medium text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
              Search & Filters
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-white mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search checklists..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-black/40 border-cyan-500/30"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-white mb-1 block">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-content"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => handleBulkAction('archive')}
                disabled={selectedChecklists.length === 0}
                variant="secondary"
                className="w-full hover:bg-cyan-500/20 border-cyan-500/30"
              >
                <Filter className="h-4 w-4 mr-2" />
                Bulk Archive ({selectedChecklists.length})
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Checklists List */}
      <div className="space-y-4">
        {filteredChecklists.map((checklist) => (
          <Card key={checklist.id} className="group bg-gradient-to-br from-gray-900/95 via-cyan-900/20 to-gray-900/95 backdrop-blur-md border border-cyan-500/20 hover:border-cyan-400/40 transition-all duration-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.2)] hover:scale-[1.01] relative overflow-hidden">
            {/* Animated Corner Accent */}
            <div className="absolute top-0 right-0 w-32 h-32 pointer-events-none bg-gradient-to-br from-transparent via-transparent to-cyan-500/10" />

            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={selectedChecklists.includes(checklist.id)}
                    onChange={() => handleChecklistToggle(checklist.id)}
                    className="rounded border-gray-600"
                  />
                  <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 shadow-md">
                    <CheckSquare className="h-7 w-7 text-cyan-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-semibold text-cyan-200 mb-1">{checklist.name}</CardTitle>
                    <p className="text-sm text-gray-400">{checklist.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(checklist.status)}
                  <Badge variant="secondary">
                    {checklist.progress.toFixed(0)}% Complete
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 pb-4">
              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-400 uppercase text-xs tracking-wide">Progress</span>
                  <span className="text-white font-medium">
                    {checklist.items.filter(item => item.completed).length} / {checklist.items.length} items
                  </span>
                </div>
                <div className="w-full bg-gray-800/50 rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      checklist.progress === 100
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                        : checklist.progress >= 70
                        ? 'bg-gradient-to-r from-yellow-500 to-amber-500'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-500'
                    }`}
                    style={{ width: `${checklist.progress}%` }}
                  />
                </div>
              </div>

              {/* Checklist Items */}
              <div className="space-y-3">
                {checklist.items.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 p-3 bg-black/30 backdrop-blur-sm rounded-lg border border-cyan-500/10 hover:border-cyan-500/30 transition-all duration-200">
                    <button
                      onClick={() => handleItemToggle(checklist.id, item.id)}
                      className="flex-shrink-0"
                    >
                      {item.completed ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : (
                        <Circle className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-sm font-medium ${item.completed ? 'line-through text-gray-400' : 'text-white'}`}>
                          {item.title}
                        </span>
                        {getPriorityBadge(item.priority)}
                      </div>
                      <p className="text-xs text-gray-400">{item.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        {item.assignedTo && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {item.assignedTo}
                          </span>
                        )}
                        {item.dueDate && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(item.dueDate).toLocaleDateString()}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {item.category}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="secondary" className="hover:bg-cyan-500/20 border-cyan-500/30">
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="secondary" className="hover:bg-cyan-500/20 border-cyan-500/30">
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="hover:bg-red-500/20 hover:border-red-500/50">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Checklist Actions */}
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-cyan-500/20">
                <Button size="sm" variant="secondary" className="flex-1 hover:bg-cyan-500/20 border-cyan-500/30">
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
                <Button size="sm" variant="secondary" className="flex-1 hover:bg-cyan-500/20 border-cyan-500/30">
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
                <Button size="sm" variant="secondary" className="flex-1 hover:bg-cyan-500/20 border-cyan-500/30">
                  <Copy className="h-4 w-4 mr-1" />
                  Duplicate
                </Button>
                <Button size="sm" variant="ghost" className="hover:bg-red-500/20 hover:border-red-500/50">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Summary Stats */}
      <div className="bg-black/40 backdrop-blur-sm border-2 border-orange-500/30 rounded-xl p-6 shadow-2xl shadow-orange-500/20">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/30">
              <BarChart3 className="h-5 w-5 text-purple-400 drop-shadow-[0_0_8px_rgba(168,85,247,0.8)]" />
            </div>
            <h3 className="text-lg font-medium text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
              Task Statistics
            </h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 rounded-lg bg-gradient-to-br from-cyan-900/20 to-gray-900/50 border border-cyan-500/20">
              <p className="text-3xl font-bold text-cyan-400 mb-1">{checklists.length}</p>
              <p className="text-sm text-gray-400 uppercase tracking-wide">Total Checklists</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-gradient-to-br from-green-900/20 to-gray-900/50 border border-green-500/20">
              <p className="text-3xl font-bold text-green-400 mb-1">
                {checklists.filter(c => c.status === 'active').length}
              </p>
              <p className="text-sm text-gray-400 uppercase tracking-wide">Active</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-gradient-to-br from-purple-900/20 to-gray-900/50 border border-purple-500/20">
              <p className="text-3xl font-bold text-purple-400 mb-1">
                {checklists.reduce((sum, c) => sum + c.items.length, 0)}
              </p>
              <p className="text-sm text-gray-400 uppercase tracking-wide">Total Items</p>
            </div>
            <div className="text-center p-4 rounded-lg bg-gradient-to-br from-blue-900/20 to-gray-900/50 border border-blue-500/20">
              <p className="text-3xl font-bold text-blue-400 mb-1">
                {checklists.reduce((sum, c) => sum + c.items.filter(item => item.completed).length, 0)}
              </p>
              <p className="text-sm text-gray-400 uppercase tracking-wide">Completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-black/40 backdrop-blur-sm border-2 border-orange-500/30 rounded-xl p-6 shadow-2xl shadow-orange-500/20">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-orange-500/10 border border-orange-500/30">
              <Target className="h-5 w-5 text-orange-400 drop-shadow-[0_0_8px_rgba(251,146,60,0.8)]" />
            </div>
            <h3 className="text-lg font-medium text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
              Quick Actions
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button variant="secondary" className="hover:bg-cyan-500/20 border-cyan-500/30 hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] transition-all duration-300">
              <Plus className="h-4 w-4 mr-2" />
              Create New Checklist
            </Button>
            <Button variant="secondary" className="hover:bg-purple-500/20 border-purple-500/30 hover:shadow-[0_0_20px_rgba(168,85,247,0.3)] transition-all duration-300">
              <FileText className="h-4 w-4 mr-2" />
              Import Checklist
            </Button>
            <Button variant="secondary" className="hover:bg-pink-500/20 border-pink-500/30 hover:shadow-[0_0_20px_rgba(236,72,153,0.3)] transition-all duration-300">
              <Users className="h-4 w-4 mr-2" />
              Assign Tasks
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
